import { skipToken } from '@reduxjs/toolkit/dist/query'
import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ClaimRecord, ClaimRecordStatus } from '@typesApp/ClaimRecord'
import { useGetClaimRecordQuery, useLazyGetClaimRecordQuery } from '@redux/queries/claimRecords'
import { claimRecordDeletedSelector, claimRecordIdSelector } from '@redux/slices/claimStatusSlice'
import { isClosedOrDeletedStatus, isNullifiedStatus } from '@utils/claimUtil'
import { isResourceNotFoundError } from '@utils/errorUtil'
import { resetClaimRecordState } from '@utils/resetClaimRecordState'

export type ActiveResult = {
  isActive: boolean
  claimRecord?: ClaimRecord
}

type UseClaimRecordReturn = {
  claimRecordId: string
  claimRecord?: ClaimRecord
  claimRecordStatus?: ClaimRecordStatus
  claimRecordDeleted?: boolean
  isLoading?: boolean
  isError?: boolean
  fetchClaimRecord: (preferCache?: boolean) => Promise<ClaimRecord | null> | never
  fetchClaimRecordById: (id: string) => Promise<ClaimRecord>
  precheckActive: () => Promise<ActiveResult>
}

export const useClaimRecord = (): UseClaimRecordReturn => {
  const dispatch = useDispatch()
  const claimRecordId = useSelector(claimRecordIdSelector)
  const claimRecordDeleted = useSelector(claimRecordDeletedSelector)

  const { data, isError, isFetching, isLoading, isUninitialized } = useGetClaimRecordQuery(claimRecordId || skipToken)
  const [triggerFetch] = useLazyGetClaimRecordQuery()

  const fetchClaimRecord = useCallback(
    async (preferCache?: boolean): Promise<ClaimRecord | null> | never => {
      if (!claimRecordId) return null

      try {
        const claimRecord = await triggerFetch(claimRecordId, preferCache).unwrap()

        const isLogicallyClosed = isNullifiedStatus(claimRecord?.status) || isClosedOrDeletedStatus(claimRecord?.status)
        if (isLogicallyClosed) {
          resetClaimRecordState(dispatch)
          return null
        }

        return claimRecord
      } catch (error) {
        if (isResourceNotFoundError(error)) {
          resetClaimRecordState(dispatch)
          return null
        } else {
          throw error
        }
      }
    },
    [dispatch, triggerFetch, claimRecordId]
  )

  const fetchClaimRecordById = useCallback(
    async (id: string): Promise<ClaimRecord> => {
      return await triggerFetch(id).unwrap()
    },
    [triggerFetch]
  )

  const precheckActive = useCallback(async (): Promise<ActiveResult> => {
    const claimRecord = await triggerFetch(claimRecordId).unwrap()
    const isLogicallyClosed = isNullifiedStatus(claimRecord?.status) || isClosedOrDeletedStatus(claimRecord?.status)

    if (!claimRecord || isLogicallyClosed) {
      resetClaimRecordState(dispatch)
      return { isActive: false, claimRecord }
    }
    return { isActive: true, claimRecord }
  }, [dispatch, triggerFetch, claimRecordId])

  // Note: on resetApiState (e.g. when session expired), the cached value may not reset.
  // But the isUninitialized value will be reset to true and we can use this flag to
  // return undefined instead of data so that components will get the latest value.
  //
  // Using currentData instead of data also provides the 'latest' value but it has
  // side effects on the UI.
  //
  // see: https://redux-toolkit.js.org/rtk-query/api/created-api/api-slice-utils#resetapistate

  return {
    claimRecordId,
    claimRecordDeleted,
    claimRecordStatus: isUninitialized ? undefined : data?.status,
    claimRecord: isUninitialized ? undefined : data,
    isError,
    isLoading: isFetching || isLoading,
    fetchClaimRecord,
    fetchClaimRecordById,
    precheckActive,
  }
}
